interface MemberData {
  id: string;
  name: string;
  profileImage?: string;
}

interface GroupMember {
  userId:
    | string
    | {
        _id: string;
        id?: string;
        name: string;
        profileImage?: string;
      };
  name?: string;
  profileImage?: string;
  joinedAt?: string;
}

/**
 * Extracts consistent member data from various member object formats
 * Handles both populated and non-populated userId fields
 */
export function extractMemberData(
  member: GroupMember,
  index?: number
): MemberData {
  let memberId: string;
  let memberName: string;
  let profileImage: string | undefined;

  if (typeof member.userId === 'string' && member.userId) {
    // userId is just an ID string (non-empty)
    memberId = member.userId;
    // Check for name directly on member object (API format)
    memberName = member.name || `Member ${memberId.slice(-4)}`;
    profileImage = member.profileImage;
  } else if (member.userId && typeof member.userId === 'object') {
    // userId is populated with user data
    memberId = member.userId._id || member.userId.id || `member-${index || 0}`;
    memberName = member.userId.name || `Member ${memberId.slice(-4)}`;
    profileImage = member.userId.profileImage;
  } else {
    // Fallback - might be missing userId
    memberId = `member-${index || 0}`;
    memberName = member.name || `Member ${memberId.slice(-4)}`;
    profileImage = member.profileImage;
  }

  return {
    id: memberId,
    name: memberName,
    profileImage,
  };
}

const byName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/** A to Z by name, ignoring case and accents; returns a new array. */
export function sortedByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => byName(a.name, b.name));
}

/**
 * The expense form's people pickers: you, everyone else A to Z, then the
 * spots saved during this form session in the order they were made, so a
 * new spot lands where the "Save a spot" control sat instead of jumping
 * into the middle of the list.
 */
export function orderPickerMembers<
  T extends { userId: string; userName: string },
>(
  members: T[],
  opts: { currentUserId: string; newIds: readonly string[] }
): T[] {
  const { currentUserId, newIds } = opts;
  const byId = new Map(members.map(m => [m.userId, m]));
  const tail: T[] = [];
  const placed = new Set<string>([currentUserId]);
  for (const id of newIds) {
    const m = byId.get(id);
    if (!m || placed.has(id)) continue;
    placed.add(id);
    tail.push(m);
  }
  const you = members.filter(m => m.userId === currentUserId);
  const rest = members
    .filter(m => !placed.has(m.userId))
    .sort((a, b) => byName(a.userName, b.userName));
  return [...you, ...rest, ...tail];
}
